import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Database, ArrowRight,
  AlertTriangle, CheckCircle, Zap,
  Scale, Briefcase,
  GitMerge, Lock, TrendingUp, Radio,
  ChevronRight, Building2, Landmark, Shield, Search
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { SEOHead } from '@/components/SEOHead'
import { ProductPageNav, ProductPageFooter } from '@/components/ProductPageNav'

// ── Inline SVG illustrations ────────────────────────────────────────────────

function PipelineFlowSVG() {
  const steps = [
    { label: 'Seed Input', sub: 'Email / Phone / Name / PAN', color: '#22c55e' },
    { label: 'CDD', sub: 'Identity enrichment', color: '#3b82f6' },
    { label: 'Risk Score', sub: 'Composite scoring', color: '#f59e0b' },
    { label: 'EDD', sub: 'Deep-dive investigation', color: '#ef4444' },
    { label: 'Dossier', sub: 'Exportable report', color: '#8b5cf6' },
  ]

  return (
    <svg viewBox="0 0 760 120" className="w-full max-w-3xl mx-auto" aria-label="AML pipeline flow">
      {/* connector line */}
      <line x1="60" y1="60" x2="700" y2="60" stroke="#1f2d1f" strokeWidth="2" strokeDasharray="6 4" />

      {steps.map((s, i) => {
        const cx = 60 + i * 160
        return (
          <g key={s.label}>
            {/* arrow between nodes */}
            {i > 0 && (
              <polygon
                points={`${cx - 20},56 ${cx - 8},60 ${cx - 20},64`}
                fill="#374151"
              />
            )}
            {/* node circle */}
            <circle cx={cx} cy={60} r={28} fill="#0d1a0d" stroke={s.color} strokeWidth="2" />
            <circle cx={cx} cy={60} r={22} fill={`${s.color}18`} />
            {/* step number */}
            <text x={cx} y={65} textAnchor="middle" fill={s.color} fontSize="14" fontWeight="bold">{i + 1}</text>
            {/* label */}
            <text x={cx} y={103} textAnchor="middle" fill="#e2e8f0" fontSize="11" fontWeight="600">{s.label}</text>
            <text x={cx} y={116} textAnchor="middle" fill="#64748b" fontSize="9">{s.sub}</text>
          </g>
        )
      })}
    </svg>
  )
}

function CDDLayersSVG() {
  const layers = [
    { icon: '🌐', label: 'Breach Database', sub: '12B+ leaked records', y: 30 },
    { icon: '📰', label: 'Adverse Media', sub: 'Global news NLP scan', y: 90 },
    { icon: '⚖️', label: 'Sanctions & PEP', sub: 'UN / OFAC / EU lists', y: 150 },
    { icon: '🏢', label: 'Corporate Registry', sub: 'GST / MCA / CIN lookup', y: 210 },
    { icon: '📱', label: 'Social Accounts', sub: '900+ platform probes', y: 270 },
    { icon: '💳', label: 'Payment Footprint', sub: 'UPI / VPA / NMC trace', y: 330 },
  ]

  return (
    <svg viewBox="0 0 520 380" className="w-full max-w-lg mx-auto" aria-label="CDD data layers">
      {/* center entity */}
      <circle cx="260" cy="190" r="50" fill="#0d1a0d" stroke="#22c55e" strokeWidth="2" />
      <text x="260" y="186" textAnchor="middle" fill="#22c55e" fontSize="11" fontWeight="700">SUBJECT</text>
      <text x="260" y="200" textAnchor="middle" fill="#64748b" fontSize="9">Identity Cluster</text>

      {layers.map((l, i) => {
        const angle = (i * 60 - 90) * (Math.PI / 180)
        const lx = 260 + 170 * Math.cos(angle)
        const ly = 190 + 170 * Math.sin(angle)
        const mx = 260 + 100 * Math.cos(angle)
        const my = 190 + 100 * Math.sin(angle)
        const color = ['#22c55e', '#3b82f6', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4'][i]
        return (
          <g key={l.label}>
            <line x1={mx} y1={my} x2={lx} y2={ly} stroke={`${color}40`} strokeWidth="1.5" strokeDasharray="4 3" />
            <circle cx={lx} cy={ly} r="38" fill="#0d1117" stroke={color} strokeWidth="1.5" />
            <text x={lx} y={ly - 6} textAnchor="middle" fontSize="14">{l.icon}</text>
            <text x={lx} y={ly + 8} textAnchor="middle" fill="#e2e8f0" fontSize="9" fontWeight="600">{l.label}</text>
            <text x={lx} y={ly + 20} textAnchor="middle" fill="#64748b" fontSize="8">{l.sub}</text>
          </g>
        )
      })}
    </svg>
  )
}

function RiskScoreSVG({ score = 72 }: { score?: number }) {
  const radius = 54
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (score / 100) * circumference
  const color = score >= 75 ? '#ef4444' : score >= 50 ? '#f59e0b' : '#22c55e'
  const label = score >= 75 ? 'HIGH RISK' : score >= 50 ? 'MEDIUM RISK' : 'LOW RISK'

  return (
    <svg viewBox="0 0 160 160" className="w-36 h-36 mx-auto" aria-label={`Risk score ${score}`}>
      <circle cx="80" cy="80" r={radius} fill="none" stroke="#1f2d1f" strokeWidth="10" />
      <circle
        cx="80" cy="80" r={radius}
        fill="none"
        stroke={color}
        strokeWidth="10"
        strokeDasharray={`${circumference}`}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform="rotate(-90 80 80)"
      />
      <text x="80" y="74" textAnchor="middle" fill={color} fontSize="26" fontWeight="800">{score}</text>
      <text x="80" y="90" textAnchor="middle" fill="#94a3b8" fontSize="9">/100</text>
      <text x="80" y="106" textAnchor="middle" fill={color} fontSize="9" fontWeight="700">{label}</text>
    </svg>
  )
}

function PivotChainSVG() {
  const nodes = [
    { x: 80, y: 100, label: 'email@domain.com', type: 'email', color: '#3b82f6' },
    { x: 240, y: 40, label: '+91 98765 43210', type: 'phone', color: '#22c55e' },
    { x: 240, y: 160, label: 'John Doe', type: 'name', color: '#f59e0b' },
    { x: 400, y: 100, label: '@johndoe', type: 'social', color: '#8b5cf6' },
    { x: 560, y: 60, label: 'LinkedIn Profile', type: 'profile', color: '#ec4899' },
    { x: 560, y: 140, label: 'Facebook Account', type: 'profile', color: '#06b6d4' },
  ]
  const edges = [
    [0, 1], [0, 2], [1, 3], [2, 3], [3, 4], [3, 5],
  ]

  return (
    <svg viewBox="0 0 640 200" className="w-full max-w-2xl mx-auto" aria-label="Pivot chain graph">
      {edges.map(([a, b], i) => (
        <line
          key={i}
          x1={nodes[a].x} y1={nodes[a].y}
          x2={nodes[b].x} y2={nodes[b].y}
          stroke="#1e3a1e" strokeWidth="1.5" strokeDasharray="5 3"
        />
      ))}
      {nodes.map((n, i) => (
        <g key={i}>
          <circle cx={n.x} cy={n.y} r="30" fill="#0d1117" stroke={n.color} strokeWidth="1.5" />
          <circle cx={n.x} cy={n.y} r="24" fill={`${n.color}12`} />
          <text x={n.x} y={n.y + 4} textAnchor="middle" fill={n.color} fontSize="8" fontWeight="600">
            {n.label.length > 14 ? n.label.slice(0, 14) + '…' : n.label}
          </text>
          <text x={n.x} y={n.y + 16} textAnchor="middle" fill="#475569" fontSize="7">{n.type}</text>
        </g>
      ))}
    </svg>
  )
}

function EDDModulesSVG() {
  const modules = [
    { icon: '🔴', label: 'Adverse Media', sub: 'NLP article scan', color: '#ef4444' },
    { icon: '⚖️', label: 'Enforcement', sub: 'Court & regulatory', color: '#f59e0b' },
    { icon: '🌐', label: 'Social Discovery', sub: 'holehe + social-analyzer', color: '#8b5cf6' },
    { icon: '💼', label: 'Commerce Intel', sub: 'Domain / GST / UPI', color: '#06b6d4' },
    { icon: '👤', label: 'LinkedIn Profile', sub: 'Role & employer verify', color: '#3b82f6' },
    { icon: '🏦', label: 'Breach Intel', sub: 'Identity exposure scan', color: '#22c55e' },
  ]
  const cols = 3
  const boxW = 160
  const boxH = 70
  const gapX = 20
  const gapY = 16

  return (
    <svg viewBox={`0 0 ${cols * (boxW + gapX) - gapX} ${Math.ceil(modules.length / cols) * (boxH + gapY) - gapY}`}
      className="w-full max-w-2xl mx-auto" aria-label="EDD modules">
      {modules.map((m, i) => {
        const col = i % cols
        const row = Math.floor(i / cols)
        const x = col * (boxW + gapX)
        const y = row * (boxH + gapY)
        return (
          <g key={m.label}>
            <rect x={x} y={y} width={boxW} height={boxH} rx="8" fill="#0d1117" stroke={m.color} strokeWidth="1.2" />
            <rect x={x} y={y} width="4" height={boxH} rx="2" fill={m.color} />
            <text x={x + 16} y={y + 28} fontSize="18">{m.icon}</text>
            <text x={x + 16} y={y + 46} fill="#e2e8f0" fontSize="11" fontWeight="600">{m.label}</text>
            <text x={x + 16} y={y + 59} fill="#64748b" fontSize="9">{m.sub}</text>
          </g>
        )
      })}
    </svg>
  )
}

// ── Page component ──────────────────────────────────────────────────────────

export default function AMLScreening() {
  const fadeUp = {
    hidden: { opacity: 0, y: 32 },
    visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5 } }),
  }

  return (
    <>
      <SEOHead
        title="AML Profiler — Customer Screening & Enhanced Due Diligence | Sycek OSINT"
        description="Automate KYC/AML customer screening with Sycek's AML Profiler. CDD enrichment, EDD deep-dive, breach intelligence, adverse media, sanctions screening, and risk scoring in a single pipeline."
        keywords="AML screening, KYC, enhanced due diligence, EDD, CDD, sanctions screening, PEP screening, adverse media, breach intelligence, FATF compliance, PMLA, customer risk assessment"
        canonical="/aml-profiler"
      />

      <div className="min-h-screen bg-[#0a0f0a] text-white overflow-x-hidden">

        {/* Background grid */}
        <div
          className="fixed inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage: `
              linear-gradient(rgba(34,197,94,0.2) 1px, transparent 1px),
              linear-gradient(90deg, rgba(34,197,94,0.2) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />

        <ProductPageNav activePage="AML Profiler" />

        {/* ── Hero ──────────────────────────────────────────────────────────── */}
        <section className="relative z-10 pt-20 pb-16 px-6">
          <div className="container mx-auto max-w-5xl text-center">
            <motion.div initial="hidden" animate="visible" variants={fadeUp}>
              <Badge className="mb-6 bg-green-500/10 text-green-400 border border-green-500/25 text-xs uppercase tracking-widest px-4 py-1.5">
                AML Profiler — Customer Screening &amp; EDD
              </Badge>
            </motion.div>

            <motion.h1
              initial="hidden" animate="visible" custom={1} variants={fadeUp}
              className="text-5xl md:text-6xl font-bold leading-tight mb-6">
              <span className="text-white">Stop Onboarding</span>
              <br />
              <span className="text-green-400 drop-shadow-[0_0_30px_rgba(34,197,94,0.6)]">Financial Crime Risks</span>
            </motion.h1>

            <motion.p
              initial="hidden" animate="visible" custom={2} variants={fadeUp}
              className="text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed mb-10">
              Sycek AML Profiler runs a full Customer Due Diligence (CDD) enrichment and Enhanced Due Diligence (EDD) investigation pipeline on any identity seed — email, phone, name, PAN, or GST — and delivers a risk-scored dossier in minutes, not days.
            </motion.p>

            <motion.div
              initial="hidden" animate="visible" custom={3} variants={fadeUp}
              className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="bg-green-500 hover:bg-green-600 text-black font-semibold px-8 shadow-lg shadow-green-500/40">
                  Start Screening
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/app/profiler">
                <Button size="lg" variant="outline" className="border-white/[0.15] text-slate-300 hover:text-white hover:bg-white/[0.05]">
                  Open Profiler
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>

        {/* ── Stats bar ─────────────────────────────────────────────────────── */}
        <section className="relative z-10 py-10 border-y border-white/[0.06] bg-white/[0.02]">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {[
                { value: '12B+', label: 'Breach records indexed' },
                { value: '900+', label: 'Social platforms probed' },
                { value: '60s', label: 'Avg. CDD enrichment time' },
                { value: 'FATF / PMLA', label: 'Compliance alignment' },
              ].map((s) => (
                <div key={s.label}>
                  <div className="text-3xl font-bold text-green-400 mb-1">{s.value}</div>
                  <div className="text-sm text-slate-500">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Pipeline flow ─────────────────────────────────────────────────── */}
        <section className="relative z-10 py-20 px-6">
          <div className="container mx-auto max-w-4xl">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-3">End-to-End AML Pipeline</h2>
              <p className="text-slate-400 max-w-2xl mx-auto">From a single identifier to a fully risk-scored dossier — automated, reproducible, and audit-ready.</p>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} variants={fadeUp}>
              <PipelineFlowSVG />
            </motion.div>

            {/* Step descriptions */}
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2} variants={fadeUp}
              className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-10 text-center">
              {[
                { n: '01', title: 'Seed Input', body: 'Submit any identifier — email, phone, full name, PAN, GST, or domain. The system normalises and deduplicates inputs automatically.' },
                { n: '02', title: 'CDD Enrichment', body: 'Cross-references breach databases, social profiles, corporate registries, and adverse media to build an identity cluster.' },
                { n: '03', title: 'Risk Scoring', body: 'Composite risk engine weights breach exposure, sanctions hits, adverse media volume, and network centrality into a 0–100 score.' },
                { n: '04', title: 'EDD Deep-Dive', body: 'High-risk subjects trigger six parallel EDD modules — enforcement records, court filings, social discovery, LinkedIn, commerce footprint, and more.' },
                { n: '05', title: 'Dossier Export', body: 'Full structured dossier with evidence, citations, risk verdict, and analyst notes — PDF-ready for regulators.' },
              ].map((s) => (
                <div key={s.n} className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-4">
                  <div className="text-green-500/50 text-xs font-mono mb-2">{s.n}</div>
                  <div className="text-sm font-semibold text-white mb-2">{s.title}</div>
                  <div className="text-xs text-slate-500 leading-relaxed">{s.body}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ── CDD section ───────────────────────────────────────────────────── */}
        <section className="relative z-10 py-20 px-6 bg-white/[0.015]">
          <div className="container mx-auto max-w-5xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
                <Badge className="mb-4 bg-blue-500/10 text-blue-400 border border-blue-500/25 text-xs uppercase tracking-widest">Customer Due Diligence</Badge>
                <h2 className="text-3xl font-bold text-white mb-4">360° Identity Enrichment</h2>
                <p className="text-slate-400 mb-6 leading-relaxed">
                  CDD pulls from six independent data layers simultaneously — breach records, adverse news, sanctions lists, corporate registries, social accounts, and payment footprints — then fuses them into a single deduplicated identity cluster.
                </p>
                <ul className="space-y-3">
                  {[
                    'Email / phone pivot across 12B+ breach records',
                    'Real-time adverse media scan via NLP article scoring',
                    'UN OFAC / EU / SEBI / RBI sanctions & PEP matching',
                    'GST, MCA, CIN corporate registry lookups',
                    'UPI VPA, NMC doctor registry, PAN/GST verification',
                    'Social account discovery across 900+ platforms',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm text-slate-300">
                      <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>

              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} variants={fadeUp}>
                <CDDLayersSVG />
              </motion.div>
            </div>
          </div>
        </section>

        {/* ── Risk engine section ───────────────────────────────────────────── */}
        <section className="relative z-10 py-20 px-6">
          <div className="container mx-auto max-w-5xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              {/* Risk gauge */}
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                className="bg-[#0d1117] border border-white/[0.07] rounded-2xl p-8 text-center">
                <div className="text-sm text-slate-500 uppercase tracking-widest mb-4">Sample Risk Assessment</div>
                <RiskScoreSVG score={72} />
                <div className="mt-6 grid grid-cols-3 gap-3 text-center">
                  {[
                    { label: 'Breach Exposure', val: 'HIGH', color: 'text-red-400' },
                    { label: 'Sanctions Match', val: 'NONE', color: 'text-green-400' },
                    { label: 'Adverse Media', val: '3 hits', color: 'text-amber-400' },
                  ].map((r) => (
                    <div key={r.label} className="bg-white/[0.03] rounded-lg p-2">
                      <div className={`text-sm font-bold ${r.color}`}>{r.val}</div>
                      <div className="text-[10px] text-slate-600 mt-0.5">{r.label}</div>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} variants={fadeUp}>
                <Badge className="mb-4 bg-amber-500/10 text-amber-400 border border-amber-500/25 text-xs uppercase tracking-widest">Risk Engine</Badge>
                <h2 className="text-3xl font-bold text-white mb-4">Composite Risk Scoring</h2>
                <p className="text-slate-400 mb-6 leading-relaxed">
                  Every enrichment signal contributes to a weighted 0–100 composite risk score. Weights are tuned against FATF risk categories — breach exposure, sanctions proximity, adverse media sentiment, and network centrality all feed the final verdict.
                </p>
                <div className="space-y-4">
                  {[
                    { label: 'Breach & Credential Exposure', weight: '30%', desc: 'Number of breach records, credential exposure, dark-web sightings' },
                    { label: 'Sanctions & PEP Proximity', weight: '25%', desc: 'Direct hits, second-degree network connections to sanctioned entities' },
                    { label: 'Adverse Media Volume', weight: '20%', desc: 'Article count, recency, sentiment severity (NLP-scored)' },
                    { label: 'Identity Consistency', weight: '15%', desc: 'Cross-source corroboration — mismatched details increase risk' },
                    { label: 'Network Centrality', weight: '10%', desc: 'Degree of connection to other flagged subjects in the graph' },
                  ].map((r) => (
                    <div key={r.label} className="flex items-start gap-3">
                      <span className="text-xs font-mono text-green-400 mt-0.5 w-10 flex-shrink-0">{r.weight}</span>
                      <div>
                        <div className="text-sm font-medium text-white">{r.label}</div>
                        <div className="text-xs text-slate-500">{r.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ── EDD section ───────────────────────────────────────────────────── */}
        <section className="relative z-10 py-20 px-6 bg-white/[0.015]">
          <div className="container mx-auto max-w-5xl">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-12">
              <Badge className="mb-4 bg-red-500/10 text-red-400 border border-red-500/25 text-xs uppercase tracking-widest">Enhanced Due Diligence</Badge>
              <h2 className="text-3xl font-bold text-white mb-3">Deep-Dive Investigation for High-Risk Subjects</h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                When CDD flags a subject above the risk threshold, EDD automatically activates six parallel investigation modules running 240+ seconds of enrichment.
              </p>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} variants={fadeUp} className="mb-12">
              <EDDModulesSVG />
            </motion.div>

            {/* EDD module detail cards */}
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2} variants={fadeUp}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                {
                  icon: AlertTriangle, color: 'text-red-400', borderColor: 'border-red-500/25',
                  title: 'Adverse Media',
                  items: ['Thousands of global news sources', 'NLP sentiment & entity extraction', 'Financial crime keyword matching', 'Recency-weighted scoring'],
                },
                {
                  icon: Scale, color: 'text-amber-400', borderColor: 'border-amber-500/25',
                  title: 'Enforcement & Court Records',
                  items: ['Regulatory enforcement actions', 'Court case filings', 'Sebi, RBI, ED, CBI mentions', 'Cross-jurisdiction coverage'],
                },
                {
                  icon: Radio, color: 'text-purple-400', borderColor: 'border-purple-500/25',
                  title: 'Social Account Discovery',
                  items: ['holehe — 121 platform email checks', 'social-analyzer — 900+ platforms', 'Telegram / Phunter phone probes', 'Username candidate extraction'],
                },
                {
                  icon: Building2, color: 'text-cyan-400', borderColor: 'border-cyan-500/25',
                  title: 'Commerce Footprint',
                  items: ['Domain WHOIS & SSL history', 'GST / MCA company filings', 'UPI VPA & NMC trace', 'E-commerce seller profile'],
                },
                {
                  icon: Briefcase, color: 'text-blue-400', borderColor: 'border-blue-500/25',
                  title: 'LinkedIn Intelligence',
                  items: ['Employment history extraction', 'Education & certification', 'Connection-based risk scoring', 'Role-mismatch flagging'],
                },
                {
                  icon: Database, color: 'text-green-400', borderColor: 'border-green-500/25',
                  title: 'Breach Intelligence',
                  items: ['12B+ record breach corpus', 'Credential cluster pivoting', 'Dark-web data enrichment', 'Identity cluster expansion'],
                },
              ].map((card) => (
                <div key={card.title} className={`bg-[#0d1117] border ${card.borderColor} rounded-xl p-5`}>
                  <div className="flex items-center gap-2 mb-3">
                    <card.icon className={`w-4 h-4 ${card.color}`} />
                    <span className={`text-sm font-semibold ${card.color}`}>{card.title}</span>
                  </div>
                  <ul className="space-y-1.5">
                    {card.items.map((item) => (
                      <li key={item} className="flex items-center gap-2 text-xs text-slate-400">
                        <ChevronRight className="w-3 h-3 text-slate-600 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ── Breach intelligence / pivot chain ────────────────────────────── */}
        <section className="relative z-10 py-20 px-6">
          <div className="container mx-auto max-w-5xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
                <Badge className="mb-4 bg-green-500/10 text-green-400 border border-green-500/25 text-xs uppercase tracking-widest">Breach Intelligence</Badge>
                <h2 className="text-3xl font-bold text-white mb-4">Pivot Across 12 Billion Leaked Records</h2>
                <p className="text-slate-400 mb-6 leading-relaxed">
                  A single email or phone number can unlock an entire identity graph. The PivotChain engine traces lateral connections across breach databases — discovering aliases, alternate contacts, and linked accounts that a subject may not have disclosed.
                </p>
                <ul className="space-y-3">
                  {[
                    'Email → associated phones, usernames, names',
                    'Phone → linked emails and platform accounts',
                    'Username → cross-platform profile correlation',
                    'Discardable false positives with audit trail',
                    'Confidence-scored each chain hop',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm text-slate-300">
                      <GitMerge className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>

              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} variants={fadeUp}
                className="bg-[#0d1117] border border-white/[0.07] rounded-2xl p-8">
                <div className="text-xs text-slate-500 uppercase tracking-widest mb-4 text-center">Sample Identity Pivot Graph</div>
                <PivotChainSVG />
              </motion.div>
            </div>
          </div>
        </section>

        {/* ── Compliance section ────────────────────────────────────────────── */}
        <section className="relative z-10 py-20 px-6 bg-white/[0.015]">
          <div className="container mx-auto max-w-5xl">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-12">
              <Badge className="mb-4 bg-slate-500/10 text-slate-400 border border-slate-500/25 text-xs uppercase tracking-widest">Compliance Alignment</Badge>
              <h2 className="text-3xl font-bold text-white mb-3">Built for Regulated Environments</h2>
              <p className="text-slate-400 max-w-2xl mx-auto">Sycek AML Profiler is designed around international AML/CFT compliance frameworks — providing the evidence trail regulators expect.</p>
            </motion.div>

            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} variants={fadeUp}
              className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  framework: 'FATF Recommendations',
                  color: 'border-blue-500/25 bg-blue-500/[0.03]',
                  badge: 'text-blue-400 bg-blue-500/10 border-blue-500/25',
                  points: [
                    'Risk-based CDD (Rec. 10)',
                    'Enhanced measures for high-risk (Rec. 19)',
                    'PEP identification & monitoring (Rec. 12)',
                    'Beneficial ownership (Rec. 24-25)',
                  ],
                },
                {
                  framework: 'India PMLA / RBI',
                  color: 'border-amber-500/25 bg-amber-500/[0.03]',
                  badge: 'text-amber-400 bg-amber-500/10 border-amber-500/25',
                  points: [
                    'KYC Master Direction compliance',
                    'CERSAI beneficial owner registry',
                    'PAN/Aadhaar identity verification',
                    'Unified SEBI/ED screening',
                  ],
                },
                {
                  framework: 'Audit & Evidence',
                  color: 'border-green-500/25 bg-green-500/[0.03]',
                  badge: 'text-green-400 bg-green-500/10 border-green-500/25',
                  points: [
                    'Immutable case audit trail',
                    'Source-cited findings with timestamps',
                    'Analyst notes & discard justifications',
                    'PDF dossier export for regulators',
                  ],
                },
              ].map((f) => (
                <div key={f.framework} className={`border ${f.color} rounded-xl p-6`}>
                  <Badge className={`mb-4 text-xs ${f.badge} border`}>{f.framework}</Badge>
                  <ul className="space-y-2.5">
                    {f.points.map((pt) => (
                      <li key={pt} className="flex items-start gap-2 text-sm text-slate-300">
                        <CheckCircle className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
                        {pt}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ── Who uses it ───────────────────────────────────────────────────── */}
        <section className="relative z-10 py-20 px-6">
          <div className="container mx-auto max-w-5xl">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-3">Built for Every AML Team</h2>
              <p className="text-slate-400 max-w-xl mx-auto">From compliance officers at neo-banks to investigators at financial intelligence units.</p>
            </motion.div>
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} variants={fadeUp}
              className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: Landmark, label: 'Banks & NBFCs', sub: 'Customer onboarding KYC' },
                { icon: TrendingUp, label: 'FinTechs', sub: 'Real-time risk decisioning' },
                { icon: Shield, label: 'FIU & Regulators', sub: 'STR/SAR investigation' },
                { icon: Search, label: 'Law Firms', sub: 'Litigation counterparty DD' },
              ].map((u) => (
                <div key={u.label} className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-5 text-center hover:border-green-500/25 transition-colors">
                  <u.icon className="w-6 h-6 text-green-400/60 mx-auto mb-3" />
                  <div className="text-sm font-semibold text-white mb-1">{u.label}</div>
                  <div className="text-xs text-slate-500">{u.sub}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ── CTA ───────────────────────────────────────────────────────────── */}
        <section className="relative z-10 py-24 px-6">
          <div className="container mx-auto max-w-3xl text-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <div className="bg-gradient-to-b from-green-500/10 to-transparent border border-green-500/20 rounded-3xl p-12">
                <Zap className="w-10 h-10 text-green-400 mx-auto mb-4" />
                <h2 className="text-3xl font-bold text-white mb-4">Start Screening Customers Today</h2>
                <p className="text-slate-400 mb-8 leading-relaxed">
                  Get full CDD enrichment and EDD investigation on any identity. No setup required — submit a seed and receive a risk-scored dossier in minutes.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/register">
                    <Button size="lg" className="bg-green-500 hover:bg-green-600 text-black font-semibold px-10 shadow-lg shadow-green-500/40">
                      Create Free Account
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/app/profiler">
                    <Button size="lg" variant="outline" className="border-white/[0.15] text-slate-300 hover:text-white hover:bg-white/[0.05]">
                      Open Profiler
                    </Button>
                  </Link>
                </div>
                <p className="text-xs text-slate-600 mt-6">
                  <Lock className="w-3 h-3 inline mr-1" />
                  All screening data is encrypted at rest and in transit. No raw credentials are stored.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        <ProductPageFooter />

      </div>
    </>
  )
}
